import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Accordion from '@repo/ui/accordion'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { PlusIcon, PrintIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'

import { API, constructApiUrl } from '../../../../../../api/apis.config'
import { getCenter } from '../../../../../../api/centers/get-center'
import { getCenterCurrency } from '../../../../../../api/centers/get-center-currency'
import { getOlPermissions } from '../../../../../../lib/constants/ol-permissions'
import { Paths } from '../../../../../../lib/constants/path'
import useAuth from '../../../../../../lib/hooks/use-auth'
import { useDataProps } from '../../../../../../lib/hooks/use-data-props'
import { useDownloadDocuments } from '../../../../../../lib/hooks/use-download-documents'
import useTranslate from '../../../../../../lib/hooks/use-translate'
import {
  CenterStatus,
  centerStatusMapping
} from '../../../../../../lib/types/enums/center/center-status'
import { OLTypeEnum } from '../../../../../../lib/types/enums/ol/ol-type.enum'
import { Center } from '../../../../../../lib/types/models/center/center'
import { AccordionState, DataProps } from '../../../../../../lib/types/utils'
import { ItemNotFound } from '../../../../../elements/item-not-found'
import LoadingScreen from '../../../../../elements/loading/loading-screen'
import CenterProgramsAndActivitiesPlanner from '../../../../common/center-programs-and-activities-calendar'
import CenterTeachingHallTable from '../../../../ol-common/ol-center-program/ol-center-teaching-hall/center-teaching-hall-table'
import OlCenterTeflList from '../../../../ol-common/ol-center-program/ol-center-tefl/ol-center-tefl-list'
import { centerTeflColumns } from '../../../../ol-common/ol-center-program/ol-center-tefl/utils'
import { PlannerHandle } from '../../../../ol-common/ol-center-program/ol-center-weekly-planner/ol-center-weekly-planner'
import {
  CenterProgramAccordionKey,
  getCenterProgramAccordionState
} from '../../../../ol-common/ol-center-program/utils'
import { CenterSimpleProgramAndActivities } from './center-simple-program-and-activities'

const CenterProgramAndActivities = (props: DataProps<Center>) => {
  const { t } = useTranslate()
  const { downloadDocuments } = useDownloadDocuments()
  const plannerRef = useRef<PlannerHandle>(null)
  const { navigate } = useNavigator()
  const { user } = useAuth()
  const {
    data: center,
    id,
    refetch,
    isPending
  } = useDataProps(props, (id) => getCenter(id, 'FULL'))
  const olId = center?.ol?.id
  const olType = center?.ol?.type

  const permissions = getOlPermissions({ role: user.role, type: olType })

  const { data: centerCurrency } = useQuery({
    queryKey: ['center-currency', id],
    queryFn: () => {
      if (center) return getCenterCurrency(id)
    },
    enabled: !!id
  })

  const initialAccordionState = useMemo<AccordionState<CenterProgramAccordionKey>[]>(
    () =>
      getCenterProgramAccordionState(t, {
        'program-details': center && (
          <div className="flex items-center gap-2">
            <Badge
              iconPosition="start"
              variant={centerStatusMapping[center.activitiesStatus].variant}
              text={t(centerStatusMapping[center.activitiesStatus].label)}
            />
            {center.activitiesStatus !== CenterStatus.VALIDATED && (
              <Button
                label={t('ol.center.program.activities.add.course')}
                icon={PlusIcon}
                variant="primary"
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  plannerRef.current?.openCreateCourseModal()
                }}
              />
            )}
          </div>
        )
      }),
    [t, center?.activitiesStatus]
  )

  const [accordionState, setAccordionState] =
    useState<AccordionState<CenterProgramAccordionKey>[]>(initialAccordionState)

  const renderAccordionContent = (key: CenterProgramAccordionKey) => {
    switch (key) {
      case 'program-details':
        return (
          <CenterProgramsAndActivitiesPlanner
            plannerRef={plannerRef}
            center={center}
            refetch={async () => await refetch?.()}
            currency={centerCurrency?.currency}
            mode="validate"
          />
        )
      case 'tefl':
        return (
          <OlCenterTeflList
            centerId={id}
            olId={olId}
            columns={[
              {
                accessorFn: (row) => row.tefl?.lastName,
                accessorKey: 'tefl.lastName',
                header: t('ol.center.tefl.table.lastName'),

                cell: ({ row }) => {
                  const tefl = row.original.tefl

                  return tefl ? (
                    <Typography.Link
                      variant="secondary-body"
                      onClick={(e) => {
                        e.stopPropagation()
                        if (!olId) return
                        navigate(
                          Paths.OL_TEFL_DETAILS_PAGE.replace(':olId', olId.toString()).replace(
                            ':teflId',
                            tefl.id.toString()
                          )
                        )
                      }}
                    >
                      {row.original.tefl?.lastName.toUpperCase()}
                    </Typography.Link>
                  ) : (
                    <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
                      ({t('ol.center.program.activities.tefl.not.assigned')})
                    </Typography>
                  )
                }
              },
              ...centerTeflColumns(t, false)
            ]}
          />
        )
      case 'teaching-hall':
        return (
          <CenterTeachingHallTable
            centerId={id}
            navigationPath={(teachingHall) =>
              olId
                ? Paths.OL_TEACHING_HALL_VIEW_PAGE.replace(':olId', olId?.toString()).replace(
                    ':id',
                    teachingHall.id.toString()
                  )
                : undefined
            }
          />
        )
      default:
        return null
    }
  }

  useEffect(() => {
    setAccordionState(initialAccordionState)
  }, [initialAccordionState])

  if (isPending) return <LoadingScreen />
  if (!center) return <ItemNotFound title="Le centre que vous cherchez n'existe pas" />

  if (permissions['center-program-and-activities'])
    return (
      <CenterSimpleProgramAndActivities
        data={center}
        view={olType === OLTypeEnum.RESIDENCE ? 'addresses' : 'teaching-hall'}
      />
    )

  return (
    <div className="mt-5 flex flex-col gap-3">
      <Typography variant="sub-heading" color={tailwindColors.black.DEFAULT}>
        {t('ol.center.program.activities.title')}
      </Typography>
      <div className="flex items-center gap-2 mb-3">
        <Button
          variant="ghost"
          label={t('ol.center.program.activities.print.program')}
          icon={PrintIcon}
          onClick={() =>
            downloadDocuments({
              endpoint: constructApiUrl(API.centers.printCenterActivityProgram.url, {
                id: center.id
              })
            })
          }
        />
        <Button
          variant="ghost"
          label={t('ol.center.program.activities.print.program.by.price')}
          icon={PrintIcon}
          onClick={() =>
            downloadDocuments({
              endpoint: constructApiUrl(API.centers.printCenterActivityProgramWithBudget.url, {
                id: center.id
              })
            })
          }
        />
      </div>

      {Object.values(accordionState).map(({ key, open, label, icon, actions }) => (
        <Accordion
          key={key}
          title={
            <div className="flex items-center gap-1.5">
              <Badge icon={icon} variant="warning" size="flexible" />
              <Typography variant="text" color={tailwindColors.black.DEFAULT}>
                {label}
              </Typography>
            </div>
          }
          subtitle={actions}
          open={open}
          className="bg-white-hover"
          variant="primary"
          onToggle={() =>
            setAccordionState((prev) =>
              prev.map((item) => (item.key === key ? { ...item, open: !item.open } : item))
            )
          }
        >
          {renderAccordionContent(key)}
        </Accordion>
      ))}
    </div>
  )
}

export default CenterProgramAndActivities
